import { UtilityMethods } from "@mindmemobile/mm-shared-global";
import {
  IAPIContactDetailModel,
  IAPIContactDetailPageModel,
  IAPIContactGridModel,
  IAPIContactReferenceModel,
  IAPIMindMeContactModel,
  IAPIPaymentsModel,
  IAPIPhoneModel,
  IAPISaveContactReferenceModel,
} from "../interfaces";
import { OrderRelatedGridModel } from "./OrderRelatedGridModels";
import { RelatedInvoicesGridModel } from "./RelatedInvoicesGridModel";
import { RelatedPaymentMethodGridModel } from "./RelatedPaymentMethodGridModel";
import { RelatedPaymentsGridModel } from "./RelatedPaymentsGridModel";
import { RelatedSubscriptionGridModel } from "./RelatedSubscriptionGridModel";
import { PaymentMethodModel } from "./PaymentMethodModels";

export class ContactDetailPageModel {
  detail: ContactDetailModel;
  invoices: RelatedInvoicesGridModel[];
  orders: OrderRelatedGridModel[];
  payments: RelatedPaymentsGridModel[];
  paymentMethods: RelatedPaymentMethodGridModel[];
  subscriptions: RelatedSubscriptionGridModel[];
  totalContacts: number;
  previousRecordId: string;
  nextRecordId: string;
  index: number;
  constructor(data: Partial<ContactDetailPageModel>) {
    this.detail =
      (data.detail && new ContactDetailModel(data.detail)) ||
      new ContactDetailModel({});
    this.invoices =
      data.invoices?.map((item) => new RelatedInvoicesGridModel(item)) || [];
    this.orders =
      data.orders?.map((item) => new OrderRelatedGridModel(item)) || [];
    this.payments =
      data.payments?.map((item) => new RelatedPaymentsGridModel(item)) || [];
    this.paymentMethods =
      data.paymentMethods?.map(
        (item) => new RelatedPaymentMethodGridModel(item)
      ) || [];
    this.subscriptions = data.subscriptions || [];
    this.totalContacts = data.totalContacts || 0;
    this.previousRecordId = data.previousRecordId || "";
    this.nextRecordId = data.nextRecordId || "";
    this.index = data.index || 0;
  }

  static deserializeAPIModel(
    data: IAPIContactDetailPageModel | null
  ): ContactDetailPageModel {
    const mappedData: Partial<ContactDetailPageModel> = {
      detail: data?.detail
        ? ContactDetailModel.deserializeAPIModel(data?.detail)
        : new ContactDetailModel(),
      invoices:
        data?.invoices?.map((item) =>
          RelatedInvoicesGridModel.deserializeAPIModel(item)
        ) || [],
      orders:
        data?.orders?.map((item) =>
          OrderRelatedGridModel.deserializeAPIModel(item)
        ) || [],
      payments:
        data?.payments?.map((item) =>
          RelatedPaymentsGridModel.deserializeAPIModel(item)
        ) || [],
      paymentMethods:
        data?.paymentMethods?.map((item) =>
          RelatedPaymentMethodGridModel.deserializeAPIModel(item)
        ) || [],
      subscriptions:
        data?.subscriptions.map((item) =>
          RelatedSubscriptionGridModel.deserializeAPIModel(item)
        ) || [],
      totalContacts: data?.total || 0,
      previousRecordId: data?.previousRecordId || "",
      nextRecordId: data?.nextRecordId || "",
      index: data?.index || 0,
    };
    return new ContactDetailPageModel(mappedData);
  }
}

export class ContactDetailModel {
  id: string;
  firstName: string;
  lastName: string;
  homeEmail: string;
  workEmail: string;
  otherEmail: string;
  mobilePhone?: ContactPhoneModel;
  homePhone?: ContactPhoneModel;
  workPhone?: ContactPhoneModel;
  otherPhone?: ContactPhoneModel;
  createdDate: Date;
  createdBy: string;
  accountCredits: number;
  fullName: string;
  pastDue: number;
  pastDueFrom: Date | null;
  payments: PaymentsModel | null;
  constructor(data?: Partial<ContactDetailModel>) {
    this.id = data?.id || "";
    this.firstName = data?.firstName || "";
    this.lastName = data?.lastName || "";
    this.homeEmail = data?.homeEmail || "";
    this.workEmail = data?.workEmail || "";
    this.otherEmail = data?.otherEmail || "";
    this.mobilePhone =
      data?.mobilePhone && new ContactPhoneModel(data.mobilePhone);
    this.homePhone = data?.homePhone && new ContactPhoneModel(data.homePhone);
    this.workPhone = data?.workPhone && new ContactPhoneModel(data.workPhone);
    this.otherPhone =
      data?.otherPhone && new ContactPhoneModel(data.otherPhone);
    this.createdDate = data?.createdDate || new Date();
    this.createdBy = data?.createdBy || "";
    this.accountCredits = data?.accountCredits || 0;
    this.pastDue = data?.pastDue || 0;
    this.pastDueFrom = data?.pastDueFrom || null;
    this.payments = data?.payments || null;

    this.fullName = "";
    if (this.firstName && this.lastName) {
      this.fullName = `${this.firstName} ${this.lastName}`;
    }
  }

  createContactRefrenceModel(): ContactReferenceModel {
    return new ContactReferenceModel({
      id: this.id,
      fullName: this.fullName,
      createdDate: this.createdDate,
      email: this.homeEmail,
      workEmail: this.workEmail,
      otherEmail: this.otherEmail,
      mobilePhone: this.mobilePhone,
      homePhone: this.homePhone,
      workPhone: this.workPhone,
      otherPhone: this.otherPhone,
    });
  }

  static deserializeAPIModel(data: IAPIContactDetailModel): ContactDetailModel {
    const mappedData: Partial<ContactDetailModel> = {
      id: data.id,
      firstName: data.firstName,
      lastName: data.lastName,
      homeEmail: data.homeEmail,
      workEmail: data.workEmail,
      otherEmail: data.otherEmail,
      mobilePhone:
        data.mobilePhone &&
        ContactPhoneModel.deserializeAPIModel(data.mobilePhone),
      homePhone:
        data.homePhone && ContactPhoneModel.deserializeAPIModel(data.homePhone),
      workPhone:
        data.workPhone && ContactPhoneModel.deserializeAPIModel(data.workPhone),
      otherPhone:
        data.otherPhone &&
        ContactPhoneModel.deserializeAPIModel(data.otherPhone),
      createdDate: UtilityMethods.removeZFromDateString(data.createdDate),
      createdBy: data.createdBy,
      accountCredits: data.accountCredits,
      pastDue: data.pastDue,
      pastDueFrom: data.pastDueFrom
        ? UtilityMethods.removeZFromDateString(data.pastDueFrom)
        : null,
      payments:
        data.payments && PaymentsModel.deserializeAPIModel(data.payments),
    };
    return new ContactDetailModel(mappedData);
  }
}

export class ContactPhoneModel {
  phoneNumber: string;
  countryId: string;
  countryCode: string;
  constructor(data?: Partial<ContactPhoneModel>) {
    this.phoneNumber = data?.phoneNumber || "";
    this.countryId = data?.countryId || "";
    this.countryCode = data?.countryCode || "";
  }

  static deserializeAPIModel(data: IAPIPhoneModel): ContactPhoneModel {
    const mappedData: Partial<ContactPhoneModel> = {
      phoneNumber: data.phoneNumber,
      countryId: data.countryId,
      countryCode: data.countryCode,
    };
    return new ContactPhoneModel(mappedData);
  }

  static createAPIModel(instance: ContactPhoneModel): IAPIPhoneModel {
    return {
      phoneNumber: instance.phoneNumber,
      countryId: instance.countryId,
      countryCode: instance.countryCode,
    };
  }
}

export class PaymentsModel {
  allTimes: number;
  totalInYear: number;
  totalInCurrentMonth: number;
  currentYear: string;
  currentMonth: string;

  constructor(data?: Partial<PaymentsModel>) {
    this.allTimes = data?.allTimes || 0;
    this.totalInYear = data?.totalInYear || 0;
    this.totalInCurrentMonth = data?.totalInCurrentMonth || 0;
    this.currentYear = data?.currentYear || "";
    this.currentMonth = data?.currentMonth || "";
  }

  static deserializeAPIModel(data: IAPIPaymentsModel): PaymentsModel {
    const mappedData: Partial<PaymentsModel> = {
      allTimes: data.allTimes,
      totalInYear: data.totalInYear,
      totalInCurrentMonth: data.totalInCurrentMonth,
      currentYear: data.currentYear,
      currentMonth: data.currentMonth,
    };
    return new PaymentsModel(mappedData);
  }

  createAPIModel(): IAPIPaymentsModel {
    return {
      allTimes: this.allTimes,
      totalInYear: this.totalInYear,
      totalInCurrentMonth: this.totalInCurrentMonth,
      currentYear: this.currentYear,
      currentMonth: this.currentMonth,
    };
  }
}

export class ContactGridModel {
  id: string;
  firstName: string;
  lastName: string;
  homeEmail: string;
  workEmail: string;
  otherEmail: string;
  homePhone?: ContactPhoneModel;
  workPhone?: ContactPhoneModel;
  mobilePhone?: ContactPhoneModel;
  otherPhone?: ContactPhoneModel;
  createdDate: Date;
  paymentMethod?: PaymentMethodModel;
  totalSpent: number;
  constructor(data?: Partial<ContactGridModel>) {
    this.id = data?.id || "";
    this.firstName = data?.firstName || "";
    this.lastName = data?.lastName || "";
    this.homeEmail = data?.homeEmail || "";
    this.workEmail = data?.workEmail || "";
    this.otherEmail = data?.otherEmail || "";
    this.homePhone = data?.homePhone && new ContactPhoneModel(data.homePhone);
    this.workPhone = data?.workPhone && new ContactPhoneModel(data.workPhone);
    this.otherPhone =
      data?.otherPhone && new ContactPhoneModel(data.otherPhone);
    this.mobilePhone =
      data?.mobilePhone && new ContactPhoneModel(data.mobilePhone);
    this.createdDate = data?.createdDate || new Date();
    this.paymentMethod =
      data?.paymentMethod && new PaymentMethodModel(data.paymentMethod);
    this.totalSpent = data?.totalSpent || 0;
  }

  static deserializeAPIModel(data: IAPIContactGridModel): ContactGridModel {
    const mappedData: Partial<ContactGridModel> = {
      id: data.id,
      firstName: data.firstName,
      lastName: data.lastName,
      homeEmail: data.homeEmail,
      workEmail: data.workEmail,
      otherEmail: data.otherEmail,
      homePhone:
        data.homePhone && ContactPhoneModel.deserializeAPIModel(data.homePhone),
      workPhone:
        data.workPhone && ContactPhoneModel.deserializeAPIModel(data.workPhone),
      otherPhone:
        data.otherPhone &&
        ContactPhoneModel.deserializeAPIModel(data.otherPhone),
      mobilePhone:
        data.mobilePhone &&
        ContactPhoneModel.deserializeAPIModel(data.mobilePhone),
      createdDate: UtilityMethods.removeZFromDateString(data.createdDate),
      totalSpent: data.totalSpent,
      paymentMethod:
        data.paymentMethod &&
        PaymentMethodModel.deserializeAPIModel(data.paymentMethod),
    };
    return new ContactGridModel(mappedData);
  }
}

export class ContactReferenceModel {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  workEmail: string;
  otherEmail: string;
  fullName: string;
  createdDate: Date;
  mobilePhone?: ContactPhoneModel;
  homePhone?: ContactPhoneModel;
  workPhone?: ContactPhoneModel;
  otherPhone?: ContactPhoneModel;
  mindmeContactId?: string;
  hasPaymentTransactions: boolean;
isNew?: boolean;  
  constructor(data?: Partial<ContactReferenceModel>) {
    this.id = (data && data.id) || "";
    this.firstName = (data && data.firstName) || "";
    this.lastName = (data && data.lastName) || "";
    this.email = (data && data.email) || "";
    this.fullName = `${this.firstName} ${this.lastName}`.trim();
    this.createdDate =
      (data?.createdDate && new Date(data.createdDate)) || new Date();
    this.workEmail = (data && data.workEmail) || "";
    this.otherEmail = (data && data.otherEmail) || "";
    this.mobilePhone =
      data?.mobilePhone && new ContactPhoneModel(data.mobilePhone);
    this.homePhone = data?.homePhone && new ContactPhoneModel(data.homePhone);
    this.workPhone = data?.workPhone && new ContactPhoneModel(data.workPhone);
    this.otherPhone =
      data?.otherPhone && new ContactPhoneModel(data.otherPhone);
    this.mindmeContactId = data?.mindmeContactId || "";
    this.hasPaymentTransactions = data?.hasPaymentTransactions === true;
    this.isNew = data?.isNew === true;
  }

  get displayValue(): string {
    return (
      this.fullName ||
      this.email ||
      this.workEmail ||
      this.otherEmail ||
      this.homePhone?.phoneNumber ||
      this.workPhone?.phoneNumber ||
      this.otherPhone?.phoneNumber ||
      this.mobilePhone?.phoneNumber ||
      ""
    );
  }
  get displayMobileNumber(): string {
    return (
      this.workPhone?.phoneNumber ||
      this.homePhone?.phoneNumber ||
      this.otherPhone?.phoneNumber ||
      this.mobilePhone?.phoneNumber ||
      ""
    );
  }

  get displayMobile(): ContactPhoneModel | undefined {
    return (
      this.workPhone || this.homePhone || this.otherPhone || this.mobilePhone
    );
  }

  get displayEmail(): string {
    return this.email || this.workEmail || this.otherEmail || "";
  }

  static deserializeAPIModel(
    data: IAPIContactReferenceModel
  ): ContactReferenceModel {
    if (!data) {
      return new ContactReferenceModel();
    }
    const mappedData: Partial<ContactReferenceModel> = {
      id: data.id,
      firstName: data.firstName,
      lastName: data.lastName,
      email: data.email,
      workEmail: data.workEmail,
      otherEmail: data.otherEmail,
      mobilePhone:
        data.mobilePhone &&
        ContactPhoneModel.deserializeAPIModel(data.mobilePhone),
      homePhone:
        data.homePhone && ContactPhoneModel.deserializeAPIModel(data.homePhone),
      workPhone:
        data.workPhone && ContactPhoneModel.deserializeAPIModel(data.workPhone),
      otherPhone:
        data.otherPhone &&
        ContactPhoneModel.deserializeAPIModel(data.otherPhone),
      createdDate: UtilityMethods.removeZFromDateString(data.createdDate),
    };
    return new ContactReferenceModel(mappedData);
  }

  static deserializeFormGridContacts(
    data: IAPIContactGridModel
  ): ContactReferenceModel {
    if (!data) {
      return new ContactReferenceModel();
    }
    const mappedData: Partial<ContactReferenceModel> = {
      id: data.id,
      firstName: data.firstName,
      lastName: data.lastName,
      email: data.homeEmail,
      workEmail: data.workEmail,
      otherEmail: data.otherEmail,
      mobilePhone:
        data.mobilePhone &&
        ContactPhoneModel.deserializeAPIModel(data.mobilePhone),
      homePhone:
        data.homePhone && ContactPhoneModel.deserializeAPIModel(data.homePhone),
      workPhone:
        data.workPhone && ContactPhoneModel.deserializeAPIModel(data.workPhone),
      otherPhone:
        data.otherPhone &&
        ContactPhoneModel.deserializeAPIModel(data.otherPhone),
      createdDate: UtilityMethods.removeZFromDateString(data.createdDate),
    };
    return new ContactReferenceModel(mappedData);
  }

  static deserializeFromMindMeContacts(
    data: IAPIMindMeContactModel
  ): ContactReferenceModel {
    if (!data) {
      return new ContactReferenceModel();
    }
    const mappedData: Partial<ContactReferenceModel> = {
      id: data.id,
      firstName: data.firstName,
      lastName: data.lastName,
      email: data.homeEmail,
      workEmail: data.workEmail,
      otherEmail: data.otherEmail,
      mobilePhone:
        data.mobilePhone &&
        ContactPhoneModel.deserializeAPIModel(data.mobilePhone),
      homePhone:
        data.homePhone && ContactPhoneModel.deserializeAPIModel(data.homePhone),
      workPhone:
        data.workPhone && ContactPhoneModel.deserializeAPIModel(data.workPhone),
      otherPhone:
        data.otherPhone &&
        ContactPhoneModel.deserializeAPIModel(data.otherPhone),
      mindmeContactId: data.mindMeContactId || "",
      hasPaymentTransactions: data.hasPaymentTransactions === true,
    };
    return new ContactReferenceModel(mappedData);
  }

  createAPIModel(): IAPISaveContactReferenceModel {
    const phone = this.homePhone || this.workPhone || this.otherPhone;
    return {
      email: this.email,
      phoneNumber: phone?.phoneNumber || "",
      countryCode: phone?.countryCode || "",
      countryId: phone?.countryId || "",
      firstName: this.firstName,
      lastName: this.lastName,
    };
  }
}
